/* =========================
   PEGUU — STICKER DESIGN
========================= */

(() => {
  "use strict";

  /* =========================
     CONFIGURACIÓN
  ========================== */

  const DESIGN_STORAGE_KEY = "peguu:sticker-design";

  const API_BASE_URL =
    window.PeguuApi.endpoint("customers");

  const AUTH_STORAGE_KEY =
    "peguu:customer-auth";

  const DESIGN_FILE_METADATA_KEY =
    "peguu:design-file-metadata";

  const DESIGN_FILE_DB =
    "peguu-design-files";

  const DESIGN_FILE_STORE =
    "files";

  const ALLOWED_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "webp",
    "pdf"
  ];

  const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf"
  ];

  const PREVIEWABLE_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "webp"
  ];

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  /* =========================
     ESTADO VACÍO
  ========================== */

  const createEmptyDesignState = () => ({
    uploadLater: false,
    primaryFile: null
  });

  /* =========================
     INICIALIZACIÓN
  ========================== */

  const initialize = () => {
    const root = document.querySelector(
      "[data-peguu-sticker-design]"
    );

    if (
      !root ||
      root.dataset.initialized === "true"
    ) {
      return;
    }

    root.dataset.initialized = "true";

    /* =========================
       ELEMENTOS
    ========================== */

    const primaryFileInput = root.querySelector(
      '[data-design-file-input="1"]'
    );

    const uploadLaterCheckbox = root.querySelector(
      "[data-upload-later]"
    );

    const continueButton = root.querySelector(
      "[data-continue-button]"
    );
    const designValidationAlert = root.querySelector(
      "[data-design-validation-alert]"
    );
    let continueValidationShown = false;

    const closeDesignValidationModal = () => {
      window.PeguuValidationAlert?.close();
      continueValidationShown = false;
    };

    const showDesignValidationModal = (message) => {
      window.PeguuValidationAlert?.open({
        title: "Faltan datos para continuar",
        message: "",
        fields: [message],
      });
    };

    const previewImage = root.querySelector(
      "[data-design-preview-image]"
    );

    const fileIcon = root.querySelector(
      "[data-design-file-icon]"
    );

    const uploadStatus = root.querySelector(
      "[data-design-upload-status]"
    );

    const replaceControl = root.querySelector(
      "[data-design-replace]"
    );

    const orderSummary = root.querySelector(
      "[data-peguu-order-summary]"
    );

    const scrollToOrderSummary = () => {
      if (
        !orderSummary ||
        !window.matchMedia(
          "(max-width: 767px)"
        ).matches
      ) {
        return;
      }

      const mobileViewportHeight =
        window.visualViewport?.height ||
        window.innerHeight;

      const mobileSummaryOffset =
        mobileViewportHeight * 0.336;

      const targetPosition =
        orderSummary
          .getBoundingClientRect()
          .top +
        window.scrollY -
        mobileSummaryOffset;

      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior: "smooth"
      });
    };

    /* =========================
       ESTADO LOCAL
    ========================== */

    let activePreviewUrl = null;
    let uploadAbortController = null;
    let designState = createEmptyDesignState();

    const getCustomerSession = () => {
      if (
        window.PeguuAuth &&
        typeof window.PeguuAuth.getSession ===
          "function"
      ) {
        return window.PeguuAuth.getSession();
      }

      try {
        return JSON.parse(
          localStorage.getItem(
            AUTH_STORAGE_KEY
          ) || "null"
        );
      } catch (error) {
        return null;
      }
    };

    const getCustomerToken = () => {
      return String(
        getCustomerSession()?.token || ""
      ).trim();
    };

    const saveUploadedFileMetadata = (
      metadata
    ) => {
      if (!metadata?.fileId) {
        return;
      }

      let storedMetadata = {};

      try {
        storedMetadata = JSON.parse(
          localStorage.getItem(
            DESIGN_FILE_METADATA_KEY
          ) || "{}"
        );
      } catch (error) {
        storedMetadata = {};
      }

      storedMetadata[metadata.fileId] = {
        fileId: metadata.fileId,
        fileUrl: metadata.fileUrl || null,
        fileName: metadata.fileName || null,
        fileType: metadata.fileType || null,
        extension: metadata.extension || null
      };

      localStorage.setItem(
        DESIGN_FILE_METADATA_KEY,
        JSON.stringify(storedMetadata)
      );
    };

    const openDesignFileDatabase = () => {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(
          DESIGN_FILE_DB,
          1
        );

        request.onupgradeneeded = () => {
          if (
            !request.result.objectStoreNames
              .contains(DESIGN_FILE_STORE)
          ) {
            request.result.createObjectStore(
              DESIGN_FILE_STORE
            );
          }
        };

        request.onsuccess = () =>
          resolve(request.result);
        request.onerror = () =>
          reject(request.error);
      });
    };

    const saveLocalDesignFile = async (
      file
    ) => {
      const previewKey =
        `design-${Date.now()}-${
          Math.random()
            .toString(36)
            .slice(2)
        }`;

      const database =
        await openDesignFileDatabase();

      await new Promise((resolve, reject) => {
        const transaction = database.transaction(
          DESIGN_FILE_STORE,
          "readwrite"
        );

        transaction.objectStore(
          DESIGN_FILE_STORE
        ).put(file, previewKey);

        transaction.oncomplete = resolve;
        transaction.onerror = () =>
          reject(transaction.error);
      });

      database.close();
      return previewKey;
    };

    const getLocalDesignFile = async (
      key
    ) => {
      const database =
        await openDesignFileDatabase();

      const file = await new Promise(
        (resolve, reject) => {
          const request = database
            .transaction(
              DESIGN_FILE_STORE,
              "readonly"
            )
            .objectStore(DESIGN_FILE_STORE)
            .get(key);

          request.onsuccess = () =>
            resolve(request.result || null);
          request.onerror = () =>
            reject(request.error);
        }
      );

      database.close();
      return file;
    };

    const deleteLocalDesignFile = async (
      key
    ) => {
      if (!key) {
        return;
      }

      const database =
        await openDesignFileDatabase();

      await new Promise((resolve, reject) => {
        const transaction = database.transaction(
          DESIGN_FILE_STORE,
          "readwrite"
        );

        transaction.objectStore(
          DESIGN_FILE_STORE
        ).delete(key);

        transaction.oncomplete = resolve;
        transaction.onerror = () =>
          reject(transaction.error);
      });

      database.close();
    };

    /* =========================
       PEGUU ORDER
    ========================== */

    const getOrder = () => {
      if (
        !window.PeguuOrder ||
        typeof window.PeguuOrder.get !== "function"
      ) {
        return null;
      }

      return window.PeguuOrder.get();
    };

    const updateOrder = (partialOrder) => {
      if (
        !window.PeguuOrder ||
        typeof window.PeguuOrder.update !== "function"
      ) {
        console.error(
          "PeguuOrder no está disponible."
        );

        return false;
      }

      return window.PeguuOrder.update(
        partialOrder
      );
    };

    /* =========================
       STORAGE DE DISEÑO
    ========================== */

    const getStoredDesignState = () => {
      try {
        const stored = sessionStorage.getItem(
          DESIGN_STORAGE_KEY
        );

        if (!stored) {
          return null;
        }

        const parsed = JSON.parse(stored);

        if (
          !parsed ||
          typeof parsed !== "object"
        ) {
          return null;
        }

        return parsed;
      } catch (error) {
        console.warn(
          "Peguu Diseño: no fue posible recuperar el estado guardado.",
          error
        );

        return null;
      }
    };

    const saveDesignState = () => {
      try {
        sessionStorage.setItem(
          DESIGN_STORAGE_KEY,
          JSON.stringify(designState)
        );
      } catch (error) {
        console.warn(
          "Peguu Diseño: no fue posible guardar el estado.",
          error
        );
      }
    };

    /* =========================
       HELPERS DE ELEMENTOS
    ========================== */

    const getFileInput = (index) => {
      return root.querySelector(
        `[data-design-file-input="${index}"]`
      );
    };

    const getUploadBox = (index) => {
      return root.querySelector(
        `[data-design-upload-box="${index}"]`
      );
    };

    const getRemoveFileButton = (index) => {
      return root.querySelector(
        `[data-remove-file-button="${index}"]`
      );
    };

    const getDefaultUploadText = () => {
      return "SELECCIONA UN ARCHIVO";
    };

    /* =========================
       ARCHIVOS
    ========================== */

    const getFileExtension = (fileName) => {
      if (
        !fileName ||
        typeof fileName !== "string"
      ) {
        return "";
      }

      return (
        fileName
          .split(".")
          .pop()
          ?.toLowerCase() || ""
      );
    };

    const validateFile = (file) => {
      if (!file) {
        return {
          valid: false,
          message:
            "No se seleccionó ningún archivo."
        };
      }

      const extension = getFileExtension(
        file.name
      );

      if (
        !extension ||
        !ALLOWED_EXTENSIONS.includes(extension)
      ) {
        return {
          valid: false,
          message:
            "Formato no válido. Usa JPEG, PNG, WEBP o PDF."
        };
      }

      if (
        !ALLOWED_MIME_TYPES.includes(
          String(file.type || "")
            .toLowerCase()
        )
      ) {
        return {
          valid: false,
          message:
            "Tipo de archivo no permitido. Usa JPEG, PNG, WEBP o PDF."
        };
      }

      if (file.size > MAX_FILE_SIZE) {
        return {
          valid: false,
          message:
            "El archivo supera el tamaño máximo permitido de 10 MB."
        };
      }

      return {
        valid: true,
        message: ""
      };
    };

    const createFileMetadata = (file) => {
      if (!file) {
        return null;
      }

      return {
        fileName: file.name,
        fileType: file.type || "",
        fileSize: file.size,
        extension: getFileExtension(
          file.name
        ),
        fileId: null,
        fileUrl: null,
        localPreviewKey: null,
        uploadStatus: "local"
      };
    };

    const isPreviewableFile = (
      metadata
    ) => {
      return PREVIEWABLE_EXTENSIONS.includes(
        String(
          metadata?.extension || ""
        ).toLowerCase()
      );
    };

    const setUploadStatus = (
      message,
      state = ""
    ) => {
      if (!uploadStatus) {
        return;
      }

      uploadStatus.textContent = message;
      uploadStatus.hidden = !message;
      uploadStatus.dataset.state = state;
    };

    const renderPreview = (
      metadata,
      previewUrl = ""
    ) => {
      const visualUrl =
        metadata?.fileUrl || previewUrl;

      const showImage =
        isPreviewableFile(metadata) &&
        Boolean(visualUrl);

      if (previewImage) {
        previewImage.hidden = !showImage;
        previewImage.src = showImage
          ? visualUrl
          : "";
        previewImage.alt = showImage
          ? `Previsualización de ${metadata.fileName}`
          : "";
      }

      if (fileIcon) {
        fileIcon.hidden =
          !metadata || showImage;
        fileIcon.textContent = String(
          metadata?.extension || "ARCHIVO"
        ).toUpperCase();
      }
    };

    /* =========================
       EXISTENCIA DE DISEÑOS
    ========================== */

    const hasPrimaryDesign = () => {
      return Boolean(
        primaryFileInput?.files?.[0] ||
        designState.primaryFile
      );
    };

    /* =========================
       ESTADO VISUAL
       DE LOS ARCHIVOS
    ========================== */

    const setUploadBoxLabel = (
      uploadBox,
      text
    ) => {
      if (!uploadBox) {
        return;
      }

      const textElement =
        uploadBox.querySelector(
          ".peguu-sticker-design__upload-text"
        );

      if (!textElement) {
        return;
      }

      textElement.textContent = text;
    };

    const setUploadBoxFilledState = (
      uploadBox,
      isFilled
    ) => {
      if (!uploadBox) {
        return;
      }

      uploadBox.classList.toggle(
        "is-filled",
        isFilled
      );

      if (replaceControl) {
        replaceControl.hidden = !isFilled;
      }
    };

    const setRemoveFileButtonVisibility = (
      index,
      isVisible
    ) => {
      const button =
        getRemoveFileButton(index);

      if (!button) {
        return;
      }

      button.hidden = !isVisible;
    };

    const renderStoredFile = (
      index,
      metadata
    ) => {
      if (!metadata?.fileName) {
        return;
      }

      const uploadBox =
        getUploadBox(index);

      setUploadBoxLabel(
        uploadBox,
        metadata.fileName
      );

      setUploadBoxFilledState(
        uploadBox,
        true
      );

      setRemoveFileButtonVisibility(
        index,
        true
      );
    };

    /* =========================
       PREVIEW URL
    ========================== */

    const clearPreviewUrl = () => {
      if (!activePreviewUrl) {
        return;
      }

      URL.revokeObjectURL(
        activePreviewUrl
      );

      activePreviewUrl = null;
    };

    const createPreviewUrl = (file) => {
      clearPreviewUrl();

      if (!file) {
        return "";
      }

      const extension = getFileExtension(
        file.name
      );

      if (
        !PREVIEWABLE_EXTENSIONS.includes(
          extension
        )
      ) {
        return "";
      }

      activePreviewUrl =
        URL.createObjectURL(file);

      return activePreviewUrl;
    };

    /* =========================
       SINCRONIZAR PEDIDO
    ========================== */

    const syncOrderDesign = (
      previewUrl
    ) => {
      const currentOrder = getOrder() || {};
      const currentDesign =
        currentOrder.design || {};

      const primaryFile =
        designState.primaryFile;

      let nextPreviewUrl =
        currentDesign.previewUrl || null;

      if (previewUrl !== undefined) {
        nextPreviewUrl = previewUrl;
      }

      if (!primaryFile) {
        nextPreviewUrl = null;
      }

      return updateOrder({
        design: {
          fileName:
            primaryFile?.fileName ?? null,

          fileType:
            primaryFile?.fileType ?? null,

          fileSize:
            primaryFile?.fileSize ?? null,

          extension:
            primaryFile?.extension ?? null,

          fileId:
            primaryFile?.fileId ?? null,

          fileUrl:
            primaryFile?.fileUrl ?? null,

          url:
            primaryFile?.fileUrl ?? null,

          uploadStatus:
            primaryFile?.uploadStatus ?? null,

          localPreviewKey:
            primaryFile?.localPreviewKey ?? null,

          previewUrl: nextPreviewUrl,

          uploadLater:
            designState.uploadLater === true,

          extraDesigns: false,

          additionalFiles: []
        }
      });
    };

    /* =========================
       DISEÑO PRINCIPAL
    ========================== */

    const savePrimaryDesign = (file) => {
      if (!file) {
        return;
      }

      const previewUrl =
        createPreviewUrl(file);

      designState.primaryFile =
        createFileMetadata(file);

      designState.uploadLater = false;

      saveDesignState();

      renderPreview(
        designState.primaryFile,
        previewUrl
      );

      syncOrderDesign(null);

      return previewUrl;
    };

    const uploadPrimaryDesign = async (
      file,
      previewUrl
    ) => {
      if (!file || !designState.primaryFile) {
        return false;
      }

      const token = getCustomerToken();

      uploadAbortController?.abort();
      uploadAbortController =
        new AbortController();

      designState.primaryFile.uploadStatus =
        "uploading";
      saveDesignState();
      syncOrderDesign(null);
      setUploadStatus(
        "Subiendo archivo…",
        "loading"
      );
      updateControlsState();

      if (!designState.primaryFile.localPreviewKey) {
        try {
          designState.primaryFile.localPreviewKey =
            await saveLocalDesignFile(file);
          saveDesignState();
        } catch (error) {
          console.warn(
            "Peguu Design: no fue posible guardar la previsualización local.",
            error
          );
        }
      }

      const formData = new FormData();
      formData.append("file", file);

      const headers = {
        Accept: "application/json"
      };

      if (token) {
        headers.Authorization =
          `Bearer ${token}`;
      }

      try {
      const response = await window.PeguuAuthClient.authFetch(
        `${API_BASE_URL}/me/cart/design`,
        {
            method: "POST",
            headers: {
              Accept: "application/json"
            },
            body: formData,
            signal:
              uploadAbortController.signal
          }
        );

        const payload = await response.json();

        if (
          response.status !== 201 ||
          payload?.success === false
        ) {
          throw new Error(
            payload?.message ||
            "No pudimos cargar el archivo. Elimínalo y vuelve a intentarlo."
          );
        }

        const uploadedFile =
          payload?.data?.file ||
          payload?.data ||
          payload;

        const fileId = String(
          uploadedFile?.fileId ||
          uploadedFile?.id ||
          ""
        ).trim();

        if (!fileId) {
          throw new Error(
            payload?.message ||
            "La respuesta de carga no incluyó el identificador del archivo."
          );
        }

        designState.primaryFile = {
          ...designState.primaryFile,
          fileId,
          fileUrl: String(
            uploadedFile?.url ||
            uploadedFile?.fileUrl ||
            ""
          ).trim() || null,
          uploadStatus: "uploaded"
        };

        saveUploadedFileMetadata(
          designState.primaryFile
        );

        saveDesignState();
        renderPreview(
          designState.primaryFile,
          previewUrl
        );
        syncOrderDesign(
          designState.primaryFile.fileUrl ||
          previewUrl ||
          null
        );
        setUploadStatus(
          "Archivo cargado correctamente.",
          "success"
        );
        updateControlsState();
        return true;
      } catch (error) {
        if (error.name === "AbortError") {
          return false;
        }

        designState.primaryFile.fileId = null;
        designState.primaryFile.fileUrl = null;
        designState.primaryFile.uploadStatus =
          "error";
        saveDesignState();
        syncOrderDesign(null);
        setUploadStatus(
          error.message ||
          "No pudimos cargar el archivo. Elimínalo y vuelve a intentarlo.",
          "error"
        );
        updateControlsState();
        return false;
      }
    };

    const clearPrimaryDesign = () => {
      const localPreviewKey =
        designState.primaryFile
          ?.localPreviewKey || null;

      uploadAbortController?.abort();
      uploadAbortController = null;
      clearPreviewUrl();
      void deleteLocalDesignFile(
        localPreviewKey
      );

      designState.primaryFile = null;
      designState.uploadLater = false;

      saveDesignState();

      syncOrderDesign(null);
      renderPreview(null);
      setUploadStatus("");
    };

    /* =========================
       RESET DE INPUT
    ========================== */

    const resetFileInput = (
      input,
      options = {}
    ) => {
      if (!input) {
        return;
      }

      const {
        syncOrder = true,
        clearStoredState = true
      } = options;

      const index =
        input.dataset.designFileInput;

      const uploadBox =
        getUploadBox(index);

      input.value = "";

      setUploadBoxLabel(
        uploadBox,
        getDefaultUploadText(index)
      );

      setUploadBoxFilledState(
        uploadBox,
        false
      );

      setRemoveFileButtonVisibility(
        index,
        false
      );

      if (clearStoredState) {
        designState.primaryFile = null;
      }

      if (syncOrder) {
        clearPrimaryDesign();
      }
    };

    /* =========================
       VALIDACIÓN DEL FLUJO
    ========================== */

    const getFlowValidation = () => {
      const uploadLater =
        uploadLaterCheckbox?.checked ===
        true;

      const primaryDesignExists =
        hasPrimaryDesign();

      const uploadReady =
        !primaryDesignExists ||
        designState.primaryFile
          ?.uploadStatus === "uploaded";

      if (
        !uploadLater &&
        (!primaryDesignExists ||
          !uploadReady)
      ) {
        return {
          valid: false,
          message:
            "Selecciona un archivo o marca la opción “Lo subiré más tarde”."
        };
      }

      return {
        valid: true,
        message: ""
      };
    };

    /* =========================
       ESTADO BOTÓN SIGUIENTE
    ========================== */

    const updateContinueState = () => {
      if (!continueButton) {
        return;
      }

      const valid =
        getFlowValidation().valid;

      getUploadBox("1")?.classList.toggle(
        "is-error",
        continueValidationShown && !valid
      );

      continueButton.disabled = false;

      continueButton.setAttribute(
        "aria-disabled",
        "false"
      );

      continueButton.classList.toggle(
        "is-disabled",
        false
      );

      if (valid) {
        window.PeguuValidationAlert?.close();
        if (designValidationAlert) {
          designValidationAlert.hidden = true;
        }
      }
    };

    /* =========================
       ACTUALIZAR CONTROLES
    ========================== */

    const updateUploadLaterAvailability =
      () => {
        if (!uploadLaterCheckbox) {
          return;
        }

        const primaryDesignExists =
          hasPrimaryDesign();

        if (primaryDesignExists) {
          uploadLaterCheckbox.checked = false;
          designState.uploadLater = false;
        }

        uploadLaterCheckbox.disabled =
          primaryDesignExists;

        const container =
          uploadLaterCheckbox.closest(
            ".peguu-sticker-design__later-check"
          );

        container?.classList.toggle(
          "is-disabled",
          primaryDesignExists
        );

        container?.setAttribute(
          "aria-disabled",
          String(primaryDesignExists)
        );
      };

    const updateFileUploadAvailability =
      () => {
        const uploadLater =
          uploadLaterCheckbox?.checked === true;
        const uploadBox = getUploadBox("1");

        if (primaryFileInput) {
          primaryFileInput.disabled = uploadLater;
        }

        uploadBox?.classList.toggle(
          "is-disabled",
          uploadLater
        );

        uploadBox?.setAttribute(
          "aria-disabled",
          String(uploadLater)
        );
      };

    const updateControlsState = () => {
      updateUploadLaterAvailability();
      updateFileUploadAvailability();
      updateContinueState();
    };

    /* =========================
       CAMBIO DE ARCHIVO
    ========================== */

    const handleFileChange = async (
      input
    ) => {
      if (!input) {
        return;
      }

      const index =
        input.dataset.designFileInput;

      const uploadBox =
        getUploadBox(index);

      const file =
        input.files?.[0] || null;

      if (!file) {
        resetFileInput(input);
        updateControlsState();
        return;
      }

      const validation =
        validateFile(file);

      if (!validation.valid) {
        window.alert(
          validation.message
        );

        resetFileInput(input);
        updateControlsState();
        return;
      }

      setUploadBoxLabel(
        uploadBox,
        file.name
      );

      setUploadBoxFilledState(
        uploadBox,
        true
      );

      setRemoveFileButtonVisibility(
        index,
        true
      );

      if (
        uploadLaterCheckbox?.checked
      ) {
        uploadLaterCheckbox.checked =
          false;
      }

      designState.uploadLater = false;

      const previewUrl =
        savePrimaryDesign(file);

      updateControlsState();

      const uploaded =
        await uploadPrimaryDesign(
          file,
          previewUrl
        );

      if (uploaded) {
        scrollToOrderSummary();
      }
    };

    /* =========================
       EVENTO DE ARCHIVOS
    ========================== */

    root.addEventListener(
      "change",
      (event) => {
        const input =
          event.target.closest(
            "[data-design-file-input]"
          );

        if (!input) {
          return;
        }

        void handleFileChange(input);
      }
    );

    /* =========================
       ELIMINAR ARCHIVO
    ========================== */

    root.addEventListener(
      "click",
      (event) => {
        const removeButton =
          event.target.closest(
            "[data-remove-file-button]"
          );

        if (!removeButton) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const index =
          removeButton.dataset
            .removeFileButton;

        resetFileInput(
          getFileInput(index)
        );

        updateControlsState();
      }
    );

    /* =========================
       LO SUBIRÉ MÁS TARDE
    ========================== */

    uploadLaterCheckbox?.addEventListener(
      "change",
      () => {
        const uploadLater =
          uploadLaterCheckbox.checked ===
          true;

        if (uploadLater) {
          resetFileInput(
            primaryFileInput,
            {
              syncOrder: false,
              clearStoredState: true
            }
          );

          clearPreviewUrl();

          designState.primaryFile = null;
          designState.uploadLater = true;

          saveDesignState();
          syncOrderDesign(null);
        } else {
          designState.uploadLater = false;

          saveDesignState();
          syncOrderDesign();
        }

        updateControlsState();

        if (uploadLater) {
          scrollToOrderSummary();
        }
      }
    );

    /* =========================
       CREAR ESTADO DESDE PEDIDO
    ========================== */

    const createStateFromOrder = () => {
      const order = getOrder();
      const design = order?.design;

      if (!design) {
        return null;
      }

      const hasPrimaryMetadata =
        Boolean(design.fileName);

      return {
        uploadLater:
          design.uploadLater === true,

        primaryFile:
          hasPrimaryMetadata
            ? {
                fileName:
                  design.fileName,

                fileType:
                  design.fileType || "",

                fileSize:
                  Number(
                    design.fileSize || 0
                  ),

                extension:
                  design.extension || "",

                fileId:
                  design.fileId || null,

                fileUrl:
                  design.fileUrl ||
                  design.url ||
                  null,

                localPreviewKey:
                  design.localPreviewKey ||
                  null,

                uploadStatus:
                  design.uploadStatus ||
                  (design.fileId
                    ? "uploaded"
                    : "local")
              }
            : null
      };
    };

    /* =========================
       NORMALIZAR ESTADO
    ========================== */

    const normalizeDesignState = (
      storedState
    ) => {
      const emptyState =
        createEmptyDesignState();

      if (
        !storedState ||
        typeof storedState !== "object"
      ) {
        return emptyState;
      }

      return {
        uploadLater:
          storedState.uploadLater === true,

        primaryFile:
          storedState.primaryFile &&
          storedState.primaryFile.fileName
            ? {
                fileName: String(
                  storedState.primaryFile
                    .fileName
                ),

                fileType: String(
                  storedState.primaryFile
                    .fileType || ""
                ),

                fileSize: Number(
                  storedState.primaryFile
                    .fileSize || 0
                ),

                extension: String(
                  storedState.primaryFile
                    .extension || ""
                ),

                fileId:
                  storedState.primaryFile
                    .fileId || null,

                fileUrl:
                  storedState.primaryFile
                    .fileUrl || null,

                localPreviewKey:
                  storedState.primaryFile
                    .localPreviewKey || null,

                uploadStatus:
                  storedState.primaryFile
                    .uploadStatus ||
                  (storedState.primaryFile
                    .fileId
                    ? "uploaded"
                    : "local")
              }
            : null
      };
    };

    /* =========================
       RESTAURAR ESTADO
    ========================== */

    const restoreState = () => {
      const storedState =
        getStoredDesignState();

      const fallbackState =
        storedState
          ? null
          : createStateFromOrder();

      designState =
        normalizeDesignState(
          storedState || fallbackState
        );

      if (designState.primaryFile) {
        designState.uploadLater = false;
      }

      if (uploadLaterCheckbox) {
        uploadLaterCheckbox.checked =
          designState.uploadLater === true;
      }

      if (
        !designState.uploadLater &&
        designState.primaryFile
      ) {
        renderStoredFile(
          "1",
          designState.primaryFile
        );

        renderPreview(
          designState.primaryFile,
          designState.primaryFile.fileUrl ||
          ""
        );

        if (
          designState.primaryFile
            .uploadStatus === "uploaded"
        ) {
          setUploadStatus(
            "Archivo cargado correctamente.",
            "success"
          );
        }

        if (
          designState.primaryFile
            .localPreviewKey &&
          (
            !designState.primaryFile.fileUrl ||
            !designState.primaryFile.fileId
          )
        ) {
          void getLocalDesignFile(
            designState.primaryFile
              .localPreviewKey
          ).then(async (file) => {
            if (!file || !designState.primaryFile) {
              return;
            }

            const restoredPreviewUrl =
              createPreviewUrl(file);

            renderPreview(
              designState.primaryFile,
              restoredPreviewUrl
            );

            if (!designState.primaryFile.fileId) {
              await uploadPrimaryDesign(
                file,
                restoredPreviewUrl
              );
            }
          });
        }
      }

      saveDesignState();
    };

    /* =========================
       SIGUIENTE
    ========================== */

    continueButton?.addEventListener(
      "click",
      () => {
        const validation =
          getFlowValidation();

        continueValidationShown = true;

        if (!validation.valid) {
          if (designValidationAlert) {
            designValidationAlert.hidden = true;
          }

          showDesignValidationModal(validation.message);

          updateControlsState();
          return;
        }

        closeDesignValidationModal();

        if (designValidationAlert) {
          designValidationAlert.hidden = true;
        }

        saveDesignState();

        window.location.href =
          "/pages/arma-tus-stickers-resumen";
      }
    );

    /* =========================
       INICIO
    ========================== */

    restoreState();
    updateControlsState();
  };

  /* =========================
     DOM READY
  ========================== */

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialize
    );
  } else {
    initialize();
  }

  /* =========================
     SHOPIFY THEME EDITOR
  ========================== */

  document.addEventListener(
    "shopify:section:load",
    initialize
  );
})();
